import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'
import { CnDatahubCategoryColumn, PrivateCategoryConfig, PrivateCategorySource } from './datahub-interfaces'
import {
  createDefaultDatahubCategoryAccess,
  createPrivateTitleColumn,
  isPrivateTitleColumn,
} from './datahubDefaults'
import { reindexOrderKeys, sortDatahubColumns } from './datahubOrderKey'

/**
 * Fetch private category configurations from other organisations (copy templates).
 */
export async function fetchPrivateCategorySources(
  orgaId: string,
): Promise<PrivateCategorySource[]> {
  const { data } = await axios.get<PrivateCategorySource[]>(
    `/organisations/${orgaId}/cn/datahub/privateCategoriesSources`,
  )
  return data
}

/**
 * Clone a private category config for the target orga with fresh IDs.
 * Access and columns are copied; override / shared linkage fields are cleared.
 */
export function clonePrivateCategoryConfig(
  source: PrivateCategoryConfig,
  orgaId: string,
  namePrefix = 'Copie de ',
): PrivateCategoryConfig {
  const categoryId = uuidv4()
  const accessId = uuidv4()
  const sourceName = source.category.name.trim()
  const name = sourceName ? `${namePrefix}${sourceName}` : ''

  const sortedColumns = sortDatahubColumns(source.columns)
  let columns: CnDatahubCategoryColumn[] = sortedColumns.map((col) => ({
    ...col,
    id: uuidv4(),
    categoryId,
    categoryAccessId: null,
    categoryColumnId: null,
  }))
  if (!columns.some(isPrivateTitleColumn)) {
    columns = [createPrivateTitleColumn(categoryId), ...columns]
  }
  columns = reindexOrderKeys(columns)

  return {
    category: {
      id: categoryId,
      name,
      sharedSourceTable: null,
    },
    access: {
      ...createDefaultDatahubCategoryAccess(orgaId, categoryId),
      id: accessId,
      overrideName: null,
      color: source.access.color,
      linkContactMax: source.access.linkContactMax,
      linkAttachmentMax: source.access.linkAttachmentMax,
      linkEntrepriseMax: source.access.linkEntrepriseMax,
      linkRdvMax: source.access.linkRdvMax,
      removeManualCreation: source.access.removeManualCreation ?? false,
    },
    columns,
  }
}

/**
 * Create an empty private category for the orga (always includes the mandatory title column).
 */
export function createEmptyPrivateCategory(orgaId: string): PrivateCategoryConfig {
  const categoryId = uuidv4()
  return {
    category: {
      id: categoryId,
      name: '',
      sharedSourceTable: null,
    },
    access: createDefaultDatahubCategoryAccess(orgaId, categoryId),
    columns: [createPrivateTitleColumn(categoryId)],
  }
}
